import { z } from 'zod';

/**
 * Vínculo entre uma etiqueta e a tag NFC (UID de 7 B, capacidade ≥ a da
 * NTAG213) que a carrega — o subobjeto
 * `nfc` de `tenants/{tenantId}/labels/{labelId}`.
 *
 * Origem: #900/#901 — 2026-10-09 15:08 +0200 (módulo Etiquetas inteligentes,
 * `MOD_NFC_TAGS`).
 *
 * Por quê: a tag não tem senha nem lock bits (decisão D3 do owner) — qualquer
 * celular pode regravá-la. O chip guarda só o `labelId` (URI + Text + AAR); o
 * servidor guarda o serial (UID) do chip que foi gravado pelo app. Na leitura,
 * UID × labelId divergentes denunciam a tag regravada fora do app, e a tag
 * apagada ainda se resolve pelo UID.
 *
 * Por que fica fora de `schemas.ts`: o `LabelSchema` importa este schema, e o
 * contrato do callable (`nfc.ts`) importa os branded ids de `schemas.ts`. Um
 * arquivo só fecharia o ciclo de import.
 *
 * Contrato / Dados — `labels/{labelId}.nfc`, escrito SÓ pelo callable
 * `bindNfcTag` (Admin SDK). As rules recusam qualquer valor do cliente
 * (`firestore.rules`, create e ramo de edição de rascunho). Exemplo
 * anonimizado:
 *   {
 *     "tagUid": "04A1B2C3D4E5F6",   // UID de 7 B (NTAG21x), hex MAIÚSCULO sem separador
 *     "writtenAtMillis": 1760015280000, // relógio do SERVIDOR (ms)
 *     "writtenByUid": "uid-1",       // quem encostou a tag
 *     "writeCount": 2,               // gravações desta etiqueta, regravações inclusas
 *     "clientWrittenAtMillis": 1760015100000 // gravação no aparelho (TrustedClock) — ordena vínculos
 *   }
 * `null`/ausente = etiqueta sem tag (o caso de toda etiqueta anterior ao
 * módulo). Quando a mesma tag é gravada com outra etiqueta, o callable limpa o
 * `nfc` da anterior para `null` na mesma transação — inclusive em outro
 * tenant (auditoria 2026-10-09 18:25 +0200, #900). `clientWrittenAtMillis`
 * nunca fica mais que 5 min à frente do servidor (corte no callable).
 *
 * Usado por: `LabelSchema.nfc` (`schemas.ts`); callable `bindNfcTag`
 * (`apps/functions/src/nfc/bindNfcTag.ts`); Android `LabelDto`/`Label.nfc`
 * (espelho manual), a leitura em segundo plano da tela Etiquetas; iOS pelo
 * contrato gerado.
 *
 * Arquitetura: datas em epoch-ms como o resto do `LabelSchema` (a issue
 * propunha `writtenAt` serverTimestamp; o padrão do doc é `*AtMillis`). O
 * servidor é a fonte da verdade do vínculo — a tag é cache.
 *
 * Dicas: `tagUid` é sempre normalizado para maiúsculas antes de gravar e de
 * consultar (`normalizeNfcTagUid`); a query `where('nfc.tagUid', '==', x)` é
 * de igualdade em campo único e dispensa índice composto — se um dia filtrar
 * também por `status`, o índice entra em `firestore.indexes.json` no mesmo
 * commit.
 *
 * Issues: #900 (épico), #901 (contrato e servidor), #805 (bancada).
 */

/**
 * Bytes do UID aceito (NXP NTAG213/215/216: 7 B, ISO 14443-3A). Chip com UID
 * de 4 ou 10 B é recusado pelo cliente antes de gravar.
 */
export const NFC_TAG_UID_BYTES = 7;

/**
 * UID como o servidor guarda: 14 dígitos hex maiúsculos. O Android entrega
 * `Tag.id` (bytes) e o iOS `NFCMiFareTag.identifier` (Data) — os dois clientes
 * formatam para isto antes de chamar `bindNfcTag`.
 */
export const NfcTagUidSchema = z
  .string()
  .regex(/^[0-9A-F]{14}$/, 'tagUid deve ter 14 dígitos hex maiúsculos (UID de 7 B)');
export type NfcTagUid = z.infer<typeof NfcTagUidSchema>;

/**
 * Normaliza um UID recebido (aceita minúsculas e separadores `:`/`-`/espaço,
 * como o NFC Tools mostra) para o formato gravado. Devolve `null` quando o
 * resultado não é um UID de 7 B — quem chama decide se é erro.
 */
export function normalizeNfcTagUid(raw: string): NfcTagUid | null {
  const hex = raw.replace(/[\s:-]/g, '').toUpperCase();
  const parsed = NfcTagUidSchema.safeParse(hex);
  return parsed.success ? parsed.data : null;
}

/**
 * O subobjeto `labels/{labelId}.nfc` (ver o cabeçalho deste arquivo).
 *
 * Origem: #900/#901 — 2026-10-09 15:08 +0200.
 *
 * Usado por: `LabelSchema.nfc`; callable `bindNfcTag` (único escritor — as
 * rules recusam o cliente); Android `LabelDto.toDomain` (espelho manual).
 */
export const LabelNfcSchema = z.object({
  tagUid: NfcTagUidSchema,
  writtenAtMillis: z.number().int().nonnegative(),
  writtenByUid: z.string().min(1),
  writeCount: z.number().int().positive(),
  /**
   * Quando a tag foi gravada no APARELHO, pelo relógio confiável dele
   * (`TrustedClock`: servidor > GPS > sistema). Diferente de
   * `writtenAtMillis` (chegada ao servidor), que num vínculo reenviado da
   * fila offline chega horas depois. É o que ordena dois vínculos do mesmo
   * chip — auditoria 2026-10-09 (#900, A1): sem ele, o reenvio de um vínculo
   * velho desfazia o novo. Ausente em vínculo anterior ao campo (= 0).
   */
  clientWrittenAtMillis: z.number().int().nonnegative().default(0),
});
export type LabelNfc = z.infer<typeof LabelNfcSchema>;
